import { useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/misc/Breadcrumbs'
import { StatCard } from '@/components/misc/StatCard'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Search, Plus, MoreHorizontal, Printer, Truck, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getOrders, updateOrderStatus, deleteOrder } from '@/services/mock/orders'
import { ORDER_STATUS, PAYMENT_METHOD } from '@/utils/constants'
import { Badge } from '@/components/ui/Badge'
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '@/components/ui/Modal'
import { Select, SelectItem } from '@/components/ui/Select'

export default function OrdersPage() {
  const [orders, setOrders] = useState([])
  const [page, setPage] = useState(1)
  const [size] = useState(10)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [paymentFilter, setPaymentFilter] = useState('')
  const [counts, setCounts] = useState({ all: 0, completed: 0, pending: 0, canceled: 0 })
  const [showFilter, setShowFilter] = useState(false)
  const [filterFrom, setFilterFrom] = useState('')
  const [filterTo, setFilterTo] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const [viewOpen, setViewOpen] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [exportOpen, setExportOpen] = useState(false)
  const [exportType, setExportType] = useState('details')
  const [exportFormat, setExportFormat] = useState('csv')
  const [exporting, setExporting] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteTargetIds, setDeleteTargetIds] = useState([])
  const navigate = useNavigate()

  const fetchOrders = async () => {
    setLoading(true)
    try {
      const res = await getOrders({ page, size, search, status: statusFilter, payment: paymentFilter, from: filterFrom, to: filterTo })
      setOrders(res.data)
      setTotal(res.meta.total)
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const fetchCounts = async () => {
    try {
      // fetch all orders (large size) and compute counts locally
      const res = await getOrders({ page: 1, size: 10000, search, payment: paymentFilter, from: filterFrom, to: filterTo })
      const list = res.data || []
      const c = { all: list.length, completed: 0, pending: 0, canceled: 0 }
      list.forEach(o => {
        if (o.status === ORDER_STATUS.COMPLETED) c.completed += 1
        if (o.status === ORDER_STATUS.PENDING) c.pending += 1
        if (o.status === ORDER_STATUS.CANCELED) c.canceled += 1
      })
      setCounts(c)
    } catch (err) {
      console.error('Failed to fetch counts', err)
    }
  }

  useEffect(() => {
    fetchOrders()
    // update counts whenever filters/search change as well
    fetchCounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, search, statusFilter, paymentFilter, filterFrom, filterTo])

  const handleSearchChange = (e) => {
    setSearch(e.target.value)
    setPage(1)
  }

  const handleStatusChange = (status) => {
    setStatusFilter(status)
    setPage(1)
    // clear selection when changing the visible filter for predictable UX
    setSelectedIds([])
  }

  const handleUpdateStatus = async (id, status) => {
    setLoading(true)
    try {
      await updateOrderStatus(id, status)
      await fetchOrders()
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteOrder = async (id) => {
    // Open in-app delete confirmation modal
    setDeleteTargetIds([id])
    setDeleteConfirmOpen(true)
  }

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id)
      return [...prev, id]
    })
  }

  const toggleSelectAll = () => {
    if (orders.length === 0) return
    if (selectedIds.length === orders.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(orders.map(o => o.id))
    }
  }

  const clearSelection = () => setSelectedIds([])

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return
    setDeleteTargetIds([...selectedIds])
    setDeleteConfirmOpen(true)
  }

  const openMoreActions = () => setMoreOpen(true)
  const closeMoreActions = () => setMoreOpen(false)

  const printSelectedOrders = () => {
    const selected = getSelectedOrders()
    if (!selected.length) {
      alert('Please select one or more orders to print')
      return
    }

    const popup = window.open('', '_blank')
    const billsHtml = selected.map(o => {
      const itemsHtml = (o.items || []).map(i => `<tr><td>${i.productName}</td><td>${i.quantity}</td><td>${i.price}</td><td>${i.total}</td></tr>`).join('')
      return `
        <div style="page-break-after:always;padding:20px;font-family:Arial">
          <h2>Order ${o.orderNumber}</h2>
          <p><strong>Customer:</strong> ${o.customerName} ${o.customerPhone ? `• ${o.customerPhone}` : ''}</p>
          <p><strong>Email:</strong> ${o.customerEmail}</p>
          <table style="width:100%;border-collapse:collapse;margin-top:8px"><thead><tr><th style='border:1px solid #ddd;padding:6px'>Product</th><th style='border:1px solid #ddd;padding:6px'>Qty</th><th style='border:1px solid #ddd;padding:6px'>Price</th><th style='border:1px solid #ddd;padding:6px'>Total</th></tr></thead><tbody>${itemsHtml}</tbody></table>
          <div style="margin-top:8px">Total: <strong>${o.total}</strong></div>
        </div>
      `
    }).join('\n')

    popup.document.write(`<html><head><title>Bulk Bills</title></head><body>${billsHtml}</body></html>`)
    popup.document.close()
    setTimeout(() => popup.print(), 500)
    setMoreOpen(false)
  }

  const markSelectedDelivered = async () => {
    if (selectedIds.length === 0) {
      alert('Please select one or more orders to mark delivered')
      return
    }
    setLoading(true)
    try {
      for (const id of selectedIds) {
        // eslint-disable-next-line no-await-in-loop
        await updateOrderStatus(id, ORDER_STATUS.DELIVERED)
      }
      setSelectedIds([])
      await fetchOrders()
    } catch (err) {
      console.error('Failed to mark delivered', err)
      alert('Failed to update some orders')
    } finally {
      setLoading(false)
      setMoreOpen(false)
    }
  }

  const openExportForSelected = () => {
    if (selectedIds.length === 0) {
      alert('Please select one or more orders to export')
      return
    }
    setExportType('details')
    setExportOpen(true)
    setMoreOpen(false)
  }

  const performDelete = async (ids) => {
    if (!ids || ids.length === 0) return
    setLoading(true)
    try {
      for (const id of ids) {
        // eslint-disable-next-line no-await-in-loop
        await deleteOrder(id)
      }
      // if we deleted selected ones, clear selection
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)))
      await fetchOrders()
    } catch (e) {
      console.error(e)
      alert('Failed to delete order(s)')
    } finally {
      setLoading(false)
      setDeleteConfirmOpen(false)
      setDeleteTargetIds([])
    }
  }
  
  const getSelectedOrders = () => {
    return orders.filter(o => selectedIds.includes(o.id))
  }

  const buildRowsForOrders = (ordersList) => {
    return ordersList.map(o => ({
      orderNumber: o.orderNumber,
      customerName: o.customerName,
      customerEmail: o.customerEmail,
      customerPhone: o.customerPhone || '',
      items: o.items.map(i => `${i.productName} x${i.quantity}`).join(' | '),
      subtotal: o.subtotal,
      tax: o.tax,
      shipping: o.shipping,
      total: o.total,
      status: o.status,
      paymentMethod: o.paymentMethod,
      shippingAddress: `${o.shippingAddress?.street || ''}, ${o.shippingAddress?.city || ''}, ${o.shippingAddress?.state || ''}, ${o.shippingAddress?.zipCode || ''}, ${o.shippingAddress?.country || ''}`,
      createdAt: o.createdAt,
      updatedAt: o.updatedAt || '',
      deliveredAt: o.deliveredAt || '',
    }))
  }

  const handleExport = async () => {
    const selected = getSelectedOrders()
    if (selected.length === 0) {
      alert('Please select one or more orders to export or print')
      return
    }

    setExporting(true)
    try {
      if (exportType === 'bulk') {
        // Bulk bill printing: open one popup with all order bills
        const popup = window.open('', '_blank')
        const billsHtml = selected.map(o => {
          const itemsHtml = (o.items || []).map(i => `<tr><td>${i.productName}</td><td>${i.quantity}</td><td>${i.price}</td><td>${i.total}</td></tr>`).join('')
          return `
            <div style="page-break-after:always;padding:20px;font-family:Arial">
              <h2>Order ${o.orderNumber}</h2>
              <p><strong>Customer:</strong> ${o.customerName} ${o.customerPhone ? `• ${o.customerPhone}` : ''}</p>
              <p><strong>Email:</strong> ${o.customerEmail}</p>
              <table style="width:100%;border-collapse:collapse;margin-top:8px"><thead><tr><th style='border:1px solid #ddd;padding:6px'>Product</th><th style='border:1px solid #ddd;padding:6px'>Qty</th><th style='border:1px solid #ddd;padding:6px'>Price</th><th style='border:1px solid #ddd;padding:6px'>Total</th></tr></thead><tbody>${itemsHtml}</tbody></table>
              <div style="margin-top:8px">Total: <strong>${o.total}</strong></div>
            </div>
          `
        }).join('\n')

        popup.document.write(`<html><head><title>Bulk Bills</title></head><body>${billsHtml}</body></html>`)
        popup.document.close()
        setTimeout(() => popup.print(), 500)
        setExportOpen(false)
        return
      }

      // Order Details export: CSV / XLSX / PDF / Print single combined table
      const rows = buildRowsForOrders(selected)
      const filename = `orders_export_${new Date().toISOString().slice(0,10)}`

      if (exportFormat === 'csv') {
        const header = Object.keys(rows[0] || { orderNumber: '', customerName: '', customerEmail: '', customerPhone: '', items: '', subtotal: '', tax: '', shipping: '', total: '', status: '', paymentMethod: '', shippingAddress: '', createdAt: '', updatedAt: '', deliveredAt: '' })
        const csv = [header.join(',')].concat(rows.map(r => header.map(h => `"${String(r[h] ?? '').replace(/"/g,'""')}"`).join(','))).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${filename}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      } else if (exportFormat === 'xlsx') {
        try {
          const XLSX = await import('xlsx')
          const ws = XLSX.utils.json_to_sheet(rows)
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Orders')
          XLSX.writeFile(wb, `${filename}.xlsx`)
        } catch (err) {
          console.error('XLSX export failed', err)
          alert('XLSX export requires the "xlsx" package. Install it with "npm i xlsx" or choose CSV/PDF.')
        }
      } else if (exportFormat === 'pdf' || exportFormat === 'print') {
        // Open printable table (PDF via print dialog)
        const popup = window.open('', '_blank')
        const tableHtml = `
          <html>
            <head>
              <title>Orders Export</title>
              <style>table{width:100%;border-collapse:collapse}td,th{border:1px solid #ccc;padding:6px;font-size:12px}</style>
            </head>
            <body>
              <h2>Orders Export</h2>
              <table>
                <thead>
                  <tr>${Object.keys(rows[0] || {}).map(h => `<th>${h}</th>`).join('')}</tr>
                </thead>
                <tbody>
                  ${rows.map(r => `<tr>${Object.keys(r).map(k => `<td>${String(r[k] ?? '')}</td>`).join('')}</tr>`).join('')}
                </tbody>
              </table>
            </body>
          </html>
        `
        popup.document.write(tableHtml)
        popup.document.close()
        setTimeout(() => popup.print(), 500)
      }

      setExportOpen(false)
    } catch (err) {
      console.error('Export failed', err)
      alert('Export failed. Check console for details.')
    } finally {
      setExporting(false)
    }
  }

  const printOrder = (order) => {
    const popup = window.open('', '_blank')
    const itemsHtml = (order.items || []).map(i => `<tr><td>${i.productName}</td><td>${i.quantity}</td><td>${i.price}</td><td>${i.total}</td></tr>`).join('')
    const html = `
      <html>
        <head>
          <title>Order ${order.orderNumber}</title>
          <style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse}td,th{border:1px solid #ddd;padding:8px}</style>
        </head>
        <body>
          <h2>Order ${order.orderNumber}</h2>
          <p><strong>Customer:</strong> ${order.customerName} (${order.customerPhone || ''})</p>
          <p><strong>Email:</strong> ${order.customerEmail}</p>
          <p><strong>Address:</strong> ${order.shippingAddress?.street || ''}, ${order.shippingAddress?.city || ''}, ${order.shippingAddress?.state || ''} ${order.shippingAddress?.zipCode || ''}</p>
          <table>
            <thead><tr><th>Product</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
            <tbody>${itemsHtml}</tbody>
          </table>
          <p><strong>Subtotal:</strong> ${order.subtotal}</p>
          <p><strong>Tax:</strong> ${order.tax}</p>
          <p><strong>Shipping:</strong> ${order.shipping}</p>
          <p><strong>Total:</strong> ${order.total}</p>
          <p><strong>Payment:</strong> ${order.paymentStatus}</p>
          <p><strong>Status:</strong> ${order.status}</p>
        </body>
      </html>
    `
    popup.document.write(html)
    popup.document.close()
    setTimeout(() => popup.print(), 300)
  }

  const openView = (order) => {
    setSelectedOrder(order)
    setViewOpen(true)
  }

  const closeView = () => {
    setSelectedOrder(null)
    setViewOpen(false)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Orders</h1>
          <Breadcrumbs />
        </div>
      </div>

      {/* Orders header: title, stats, controls */}
      <section className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold">Order List</h2>
            <p className="text-sm text-muted-foreground">Overview of recent orders and quick actions</p>
          </div>

            <div className="flex items-center gap-2">
              {/* <Button variant="primary" size="md" className="flex items-center gap-2" disabled title="Add Order disabled">
                <Plus className="h-4 w-4" />
                Add Order
              </Button> */}
              {/* <Button variant="ghost" size="md" className="flex items-center gap-2" onClick={openMoreActions}>
                More Action
                <MoreHorizontal className="h-4 w-4" />
              </Button> */}
            </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Total Orders" value={1240} change={14.4} changeType="percentage" format="number" />
          <StatCard title="New Orders" value={240} change={20} changeType="percentage" format="number" />
          <StatCard title="Completed Orders" value={960} change={85} changeType="percentage" format="number" />
          <StatCard title="Canceled Orders" value={87} change={-5} changeType="percentage" format="number" />
        </div>

        {/* Controls panel: tabs + search/filters */}
        <div className="bg-card border p-4 rounded-xl">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handleStatusChange('')}
                className={`px-3 py-1 rounded-full text-sm ${statusFilter === '' ? 'bg-primary text-primary-foreground' : 'bg-muted/50'}`}>
                All order ({counts.all || 0})
              </button>
              <button
                onClick={() => handleStatusChange(ORDER_STATUS.COMPLETED)}
                className={`px-3 py-1 rounded-full text-sm ${statusFilter === ORDER_STATUS.COMPLETED ? 'bg-primary text-primary-foreground' : 'bg-muted/50'}`}>
                Completed ({counts.completed || 0})
              </button>
              <button
                onClick={() => handleStatusChange(ORDER_STATUS.PENDING)}
                className={`px-3 py-1 rounded-full text-sm ${statusFilter === ORDER_STATUS.PENDING ? 'bg-primary text-primary-foreground' : 'bg-muted/50'}`}>
                Pending ({counts.pending || 0})
              </button>
              <button
                onClick={() => handleStatusChange(ORDER_STATUS.CANCELED)}
                className={`px-3 py-1 rounded-full text-sm ${statusFilter === ORDER_STATUS.CANCELED ? 'bg-primary text-primary-foreground' : 'bg-muted/50'}`}>
                Canceled ({counts.canceled || 0})
              </button>
            </div>

              <div className="flex items-center gap-2 w-full md:w-auto">
              <div className="flex-1 md:flex-none">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={search} onChange={handleSearchChange} placeholder="Search order report" className="pl-10" />
                </div>
              </div>
              
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={() => setShowFilter(true)}>Filter</Button>
              <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={() => setExportOpen(true)}>Export</Button>
              {/* <Button variant="ghost" size="sm" className="hidden sm:inline-flex" onClick={openMoreActions}><MoreHorizontal className="h-4 w-4 mr-2" />More Action</Button> */}
              <div className="hidden sm:flex items-center gap-2 ml-2">
                <div className="text-sm text-muted-foreground">{selectedIds.length} selected</div>
                <Button variant="ghost" size="sm" disabled={selectedIds.length === 0} onClick={clearSelection}>Clear</Button>
              </div>
            </div>
          </div>
        </div>

        {/* More Actions modal */}
        <Modal open={moreOpen} onOpenChange={setMoreOpen}>
          <ModalHeader onClose={closeMoreActions}>
            <ModalTitle>More Actions</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Actions apply to selected orders only. Please select orders from the table first.</p>
              <div className="flex flex-col gap-2">
                <Button onClick={printSelectedOrders}>Print Selected Orders</Button>
                <Button onClick={markSelectedDelivered}>Mark Selected Delivered</Button>
                <Button onClick={openExportForSelected}>Export Selected</Button>
                <Button variant="destructive" onClick={handleDeleteSelected}>Delete Selected</Button>
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button variant="ghost" onClick={closeMoreActions}>Close</Button>
          </ModalFooter>
        </Modal>

        {/* Orders table */}
        <div className="bg-card border p-4 rounded-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground">
                  <th className="p-3">
                    <input
                      type="checkbox"
                      checked={orders.length > 0 && selectedIds.length === orders.length}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th className="p-3">No.</th>
                  <th className="p-3">Order Id</th>
                  <th className="p-3">Product</th>
                  <th className="p-3">Date</th>
                  <th className="p-3">Price</th>
                  <th className="p-3">Payment</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-muted-foreground">Loading...</td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="p-4 text-center text-muted-foreground">No orders found</td>
                  </tr>
                ) : (
                  orders.map((order, idx) => (
                    <tr key={order.id} className="border-b last:border-b-0 hover:bg-muted/50">
                      <td className="p-3 align-middle">
                        <input type="checkbox" checked={selectedIds.includes(order.id)} onChange={() => toggleSelect(order.id)} />
                      </td>
                      <td className="p-3 align-middle">{(page - 1) * size + idx + 1}</td>
                      <td className="p-3 align-middle">{order.orderNumber}</td>
                      <td className="p-3 align-middle">
                        {order.items?.[0]?.productName}
                      </td>
                      <td className="p-3 align-middle">{new Date(order.createdAt).toLocaleDateString()}</td>
                      <td className="p-3 align-middle">{order.total.toFixed(2)}</td>
                      <td className="p-3 align-middle">
                        {order.paymentStatus === 'paid' ? (
                          <Badge variant="success">Paid</Badge>
                        ) : order.paymentStatus === 'pending' ? (
                          <Badge variant="warning">Pending</Badge>
                        ) : order.paymentStatus === 'refunded' ? (
                          <Badge variant="danger">Refunded</Badge>
                        ) : (
                          <Badge>{order.paymentStatus}</Badge>
                        )}
                      </td>
                      <td className="p-3 align-middle">
                        {order.status === ORDER_STATUS.DELIVERED ? (
                          <Badge variant="success">🚚 Delivered</Badge>
                        ) : order.status === ORDER_STATUS.COMPLETED ? (
                          <Badge variant="success">✅ Completed</Badge>
                        ) : order.status === ORDER_STATUS.PENDING ? (
                          <Badge variant="warning">⏳ Pending</Badge>
                        ) : order.status === ORDER_STATUS.CANCELED ? (
                          <Badge variant="danger">❌ Canceled</Badge>
                        ) : (
                          <Badge>{order.status}</Badge>
                        )}
                      </td>
                      <td className="p-3 align-middle">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" onClick={() => printOrder(order)}>
                            <Printer className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleUpdateStatus(order.id, ORDER_STATUS.DELIVERED)}>
                            <Truck className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDeleteOrder(order.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => openView(order)}>View</Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination simplified */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-muted-foreground">Showing {(page - 1) * size + 1} - {Math.min(page * size, total)} of {total}</div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
              <div className="px-3 py-1 rounded bg-muted text-sm">{page}</div>
              <Button variant="ghost" size="sm" disabled={page * size >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
            </div>
          </div>
        </div>
        {/* Order view modal */}
        <Modal open={viewOpen} onOpenChange={setViewOpen}>
          <ModalHeader onClose={closeView}>
            <ModalTitle>Order Details</ModalTitle>
          </ModalHeader>
          <ModalContent>
            {selectedOrder ? (
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold">{selectedOrder.orderNumber}</h3>
                  <div className="text-sm text-muted-foreground">{selectedOrder.customerName} — {selectedOrder.customerEmail} {selectedOrder.customerPhone ? `• ${selectedOrder.customerPhone}` : ''}</div>
                </div>

                <div>
                  <h4 className="font-medium">Shipping Address</h4>
                  <div className="text-sm text-muted-foreground">{selectedOrder.shippingAddress?.street}, {selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state} {selectedOrder.shippingAddress?.zipCode}, {selectedOrder.shippingAddress?.country}</div>
                </div>

                <div>
                  <h4 className="font-medium">Items</h4>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground"><th className="p-2">Product</th><th className="p-2">Qty</th><th className="p-2">Price</th><th className="p-2">Total</th></tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((it, i) => (
                        <tr key={i} className="border-b"><td className="p-2">{it.productName}</td><td className="p-2">{it.quantity}</td><td className="p-2">{it.price}</td><td className="p-2">{it.total}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end space-x-4">
                  <div className="text-sm">
                    <div>Subtotal: <strong>{selectedOrder.subtotal}</strong></div>
                    <div>Tax: <strong>{selectedOrder.tax}</strong></div>
                    <div>Shipping: <strong>{selectedOrder.shipping}</strong></div>
                    <div>Total: <strong>{selectedOrder.total}</strong></div>
                  </div>
                </div>
              </div>
            ) : (
              <div>No order selected</div>
            )}
          </ModalContent>
          <ModalFooter>
            <Button variant="ghost" onClick={closeView}>Close</Button>
            <Button onClick={() => { if (selectedOrder) printOrder(selectedOrder) }}>
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
          </ModalFooter>
        </Modal>
        {/* Filter modal */}
        <Modal open={showFilter} onOpenChange={setShowFilter}>
          <ModalHeader onClose={() => setShowFilter(false)}>
            <ModalTitle>Filter Orders</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <div>
                <p className="font-medium">Status</p>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v)} className="mt-2">
                  <SelectItem value="" onSelect={() => setStatusFilter('')}>All</SelectItem>
                  <SelectItem value={ORDER_STATUS.COMPLETED} onSelect={() => setStatusFilter(ORDER_STATUS.COMPLETED)}>Completed</SelectItem>
                  <SelectItem value={ORDER_STATUS.PENDING} onSelect={() => setStatusFilter(ORDER_STATUS.PENDING)}>Pending</SelectItem>
                  <SelectItem value={ORDER_STATUS.CANCELED} onSelect={() => setStatusFilter(ORDER_STATUS.CANCELED)}>Canceled</SelectItem>
                  <SelectItem value={ORDER_STATUS.DELIVERED} onSelect={() => setStatusFilter(ORDER_STATUS.DELIVERED)}>Delivered</SelectItem>
                </Select>
              </div>

              <div>
                <p className="font-medium">Payment Method</p>
                <Select value={paymentFilter} onValueChange={(v) => setPaymentFilter(v)} className="mt-2">
                  <SelectItem value="" onSelect={() => setPaymentFilter('')}>All</SelectItem>
                  <SelectItem value={PAYMENT_METHOD.CREDIT_CARD} onSelect={() => setPaymentFilter(PAYMENT_METHOD.CREDIT_CARD)}>Credit Card</SelectItem>
                  <SelectItem value={PAYMENT_METHOD.DEBIT_CARD} onSelect={() => setPaymentFilter(PAYMENT_METHOD.DEBIT_CARD)}>Debit Card</SelectItem>
                  <SelectItem value={PAYMENT_METHOD.PAYPAL} onSelect={() => setPaymentFilter(PAYMENT_METHOD.PAYPAL)}>PayPal</SelectItem>
                  <SelectItem value={PAYMENT_METHOD.BANK_TRANSFER} onSelect={() => setPaymentFilter(PAYMENT_METHOD.BANK_TRANSFER)}>Bank Transfer</SelectItem>
                  <SelectItem value={PAYMENT_METHOD.CASH} onSelect={() => setPaymentFilter(PAYMENT_METHOD.CASH)}>Cash</SelectItem>
                </Select>
              </div>

              <div>
                <p className="font-medium">Date Range</p>
                <div className="flex gap-2 mt-2">
                  <input type="date" className="input" value={filterFrom} onChange={(e) => setFilterFrom(e.target.value)} />
                  <input type="date" className="input" value={filterTo} onChange={(e) => setFilterTo(e.target.value)} />
                </div>
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button variant="ghost" onClick={() => {
              // Reset filters
              setStatusFilter('')
              setPaymentFilter('')
              setFilterFrom('')
              setFilterTo('')
              setShowFilter(false)
            }}>Reset</Button>
            <Button onClick={() => { setPage(1); fetchOrders(); setShowFilter(false) }}>Apply</Button>
          </ModalFooter>
        </Modal>
        {/* Export modal */}
        <Modal open={exportOpen} onOpenChange={setExportOpen}>
          <ModalHeader onClose={() => setExportOpen(false)}>
            <ModalTitle>Export Selected Orders</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <div>
                <p className="font-medium">Export Type</p>
                <div className="flex items-center gap-4 mt-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="exportType" value="details" checked={exportType==='details'} onChange={() => setExportType('details')} />
                    <span>Order Details</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="exportType" value="bulk" checked={exportType==='bulk'} onChange={() => setExportType('bulk')} />
                    <span>Bulk Bill Printing</span>
                  </label>
                </div>
              </div>

              {exportType === 'details' && (
                <div>
                  <p className="font-medium">Format</p>
                  <div className="flex items-center gap-4 mt-2">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="exportFormat" value="csv" checked={exportFormat==='csv'} onChange={() => setExportFormat('csv')} />
                      <span>CSV</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="exportFormat" value="xlsx" checked={exportFormat==='xlsx'} onChange={() => setExportFormat('xlsx')} />
                      <span>XLSX</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="exportFormat" value="pdf" checked={exportFormat==='pdf'} onChange={() => setExportFormat('pdf')} />
                      <span>PDF</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="exportFormat" value="print" checked={exportFormat==='print'} onChange={() => setExportFormat('print')} />
                      <span>Print</span>
                    </label>
                  </div>
                </div>
              )}

              <p className="text-sm text-muted-foreground">Exports apply to selected orders only. Please select orders from the table first.</p>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setExportOpen(false)}>Cancel</Button>
            <Button onClick={handleExport} loading={exporting}>Export</Button>
          </ModalFooter>
        </Modal>
        {/* Delete confirmation modal (replaces browser confirm) */}
        <Modal open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <ModalHeader onClose={() => setDeleteConfirmOpen(false)}>
            <ModalTitle>Confirm Delete</ModalTitle>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <p>Are you sure you want to delete {deleteTargetIds.length} order{deleteTargetIds.length !== 1 ? 's' : ''}? This action cannot be undone.</p>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button variant="ghost" onClick={() => { setDeleteConfirmOpen(false); setDeleteTargetIds([]) }}>Cancel</Button>
            <Button variant="destructive" onClick={() => performDelete(deleteTargetIds)}>Delete</Button>
          </ModalFooter>
        </Modal>
      </section>
    </div>
  )
}